"use client"

import * as React from "react"
import { type Editor } from "@tiptap/react"
import { isMarkInSchema } from "../../../lib/tiptap-utils"

export const FONT_COLORS = [
  { label: "Vermelho", value: "#e03e3e" },
  { label: "Laranja", value: "#d9730d" },
  { label: "Amarelo", value: "#dfab01" },
  { label: "Verde", value: "#0f7b6c" },
  { label: "Azul", value: "#0b6e99" },
  { label: "Roxo", value: "#6940a5" },
  { label: "Rosa", value: "#c14f8a" },
  { label: "Cinza", value: "#787774" },
  { label: "Preto", value: "#111827" },
]

export type FontColor = (typeof FONT_COLORS)[number]

export interface UseColorFontConfig {
  editor?: Editor | null
  fontColor?: string
  label?: string
  hideWhenUnavailable?: boolean
  onApplied?: ({ color, label }: { color: string; label: string }) => void
}

export function canSetFontColor(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false
  return isMarkInSchema("textStyle", editor)
}

export function isFontColorActive(editor: Editor | null, color?: string): boolean {
  if (!editor || !editor.isEditable) return false
  if (color) return editor.isActive("textStyle", { color })
  return editor.isActive("textStyle")
}

export function useColorFont(config: UseColorFontConfig) {
  const { editor: providedEditor, fontColor, label, hideWhenUnavailable = false, onApplied } = config
  const editor = providedEditor ?? null
  const [isVisible, setIsVisible] = React.useState(true)
  const canSet = canSetFontColor(editor)
  const isActive = isFontColorActive(editor, fontColor)

  React.useEffect(() => {
    if (!editor) return
    const update = () => {
      if (!isMarkInSchema("textStyle", editor)) {
        setIsVisible(false)
        return
      }
      setIsVisible(hideWhenUnavailable ? canSetFontColor(editor) : true)
    }
    update()
    editor.on("selectionUpdate", update)
    return () => {
      editor.off("selectionUpdate", update)
    }
  }, [editor, hideWhenUnavailable])

  const handleSetColor = React.useCallback(() => {
    if (!editor || !fontColor) return
    editor.chain().setMark("textStyle", { color: fontColor }).run()
    onApplied?.({ color: fontColor, label: label || fontColor })
  }, [editor, fontColor, label, onApplied])

  const handleRemoveColor = React.useCallback(() => {
    if (!editor) return
    editor.chain().setMark("textStyle", { color: null }).run()
    onApplied?.({ color: "", label: "Remove color" })
  }, [editor, onApplied])

  return {
    isVisible,
    isActive,
    canSetColor: canSet,
    handleSetColor,
    handleRemoveColor,
    label: label || "Cor da fonte",
  }
}
